import { useEffect, useMemo, useRef, useState } from "react"
import { router } from "@inertiajs/react"
import { AlertTriangle, CalendarIcon, Check, CircleCheck, CircleDashed, Globe, OctagonX, Play, Search, Tag, X } from "lucide-react"
import { endOfDay, endOfMonth, format, startOfMonth, subDays, subMonths } from "date-fns"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Calendar } from "@/components/ui/calendar"
import { DataTableReact } from "@/components/ui/data-table-react"
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu"
import { Input } from "@/components/ui/input"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import { RunDetailSheet } from "@/Pages/Agents/RunDetailSheet"

// Parses both "yyyy-MM-dd" (from date picker) and ISO timestamps (from bar click)
export function parseDateStr(str) {
  if (!str) return null
  return str.includes("T") ? new Date(str) : new Date(str + "T00:00:00")
}

export const PROCESSING_STATUSES = ["pending", "extracting", "requesting"]

export const STATUSES = [
  { value: "processing", label: "Processing" },
  { value: "completed", label: "Completed" },
  { value: "errored", label: "Errored" },
  { value: "failed", label: "Failed" },
]

export const RESPONSES = [
  { value: "2xx", label: "Success", variant: "success" },
  { value: "3xx", label: "Redirect", variant: "warning" },
  { value: "4xx", label: "Client error", variant: "destructive" },
  { value: "5xx", label: "Server error", variant: "destructive" },
]

export const DATE_PRESETS = [
  { label: "Today",        range: () => { const d = new Date(); return { from: d, to: d } } },
  { label: "Yesterday",    range: () => { const d = subDays(new Date(), 1); return { from: d, to: d } } },
  { label: "Last 7 days",  range: () => ({ from: subDays(new Date(), 6), to: new Date() }) },
  { label: "Last 30 days", range: () => ({ from: subDays(new Date(), 29), to: new Date() }) },
  { label: "This month",   range: () => ({ from: startOfMonth(new Date()), to: new Date() }) },
  { label: "Last month",   range: () => { const d = subMonths(new Date(), 1); return { from: startOfMonth(d), to: endOfMonth(d) } } },
]

export function RunStatusBadge({ status, className, iconOnly }) {
  const cls = [iconOnly && "size-5 p-0", className].filter(Boolean).join(" ") || undefined
  const iconCls = iconOnly ? "size-3.5! text-current" : "text-current"
  switch (status) {
    case "processing":
      return <Badge variant="pending" size="small" className={cls}><CircleDashed className={iconCls} />{!iconOnly && "Processing"}</Badge>
    case "pending":
    case "extracting":
    case "requesting":
      return <Badge variant="pending" size="small" className={cls}><CircleDashed className={iconCls} />{!iconOnly && (status === "pending" ? "Pending" : status === "extracting" ? "Extracting" : "Requesting")}</Badge>
    case "completed":
      return <Badge variant="success" size="small" className={cls}><CircleCheck className={iconCls} />{!iconOnly && "Completed"}</Badge>
    case "errored":
      return <Badge variant="warning" size="small" className={cls}><AlertTriangle className={iconCls} />{!iconOnly && "Errored"}</Badge>
    case "failed":
      return <Badge variant="destructive" size="small" className={cls}><OctagonX className={iconCls} />{!iconOnly && "Failed"}</Badge>
    case "skipped":
      return <Badge variant="outline" size="small" className={cls}>{!iconOnly && "Skipped"}</Badge>
    default:
      return <Badge variant="outline" size="small" className={cls}>{!iconOnly && status}</Badge>
  }
}

export function usePagination({ page, pages, siblingCount = 1 }) {
  return useMemo(() => {
    const range = (start, end) => Array.from({ length: end - start + 1 }, (_, i) => start + i)

    if (pages <= 7) return range(1, pages)

    const leftSibling = Math.max(page - siblingCount, 1)
    const rightSibling = Math.min(page + siblingCount, pages)
    const showLeftEllipsis = leftSibling > 2
    const showRightEllipsis = rightSibling < pages - 1

    if (!showLeftEllipsis && showRightEllipsis) {
      return [...range(1, 3 + 2 * siblingCount), "...", pages]
    }
    if (showLeftEllipsis && !showRightEllipsis) {
      return [1, "...", ...range(pages - (3 + 2 * siblingCount) + 1, pages)]
    }
    return [1, "...", ...range(leftSibling, rightSibling), "...", pages]
  }, [page, pages, siblingCount])
}

export const COLUMNS = [
  {
    accessorKey: "status",
    header: "Status",
    cell: (value) => <RunStatusBadge status={value} />,
  },
  {
    accessorKey: "email_subject",
    header: "Subject",
    cell: (value) => (
      <span className="truncate max-w-[200px] block" title={value}>
        {value || "—"}
      </span>
    ),
  },
  {
    accessorKey: "email_from",
    header: "From",
    cell: (value) => (
      <span className="truncate max-w-[180px] block" title={value}>
        {value || "—"}
      </span>
    ),
  },
  {
    accessorKey: "response_status",
    header: "Response",
    cell: (value, row) => {
      if (row.status === "failed") {
        return (
          <Tooltip>
            <TooltipTrigger asChild>
              <span>
                <Badge variant="destructive" size="small">Error</Badge>
              </span>
            </TooltipTrigger>
            <TooltipContent className="max-w-xs">{row.error_message || "Unknown error"}</TooltipContent>
          </Tooltip>
        )
      }
      if (!value) return <span className="text-muted-foreground">—</span>
      const variant = value >= 200 && value < 300 ? "success" : value >= 400 ? "destructive" : "warning"
      return <Badge variant={variant} size="small">{value}</Badge>
    },
  },
  {
    accessorKey: "created_at",
    header: "Time",
    cell: (value) => (
      <span className="whitespace-nowrap">
        {new Date(value).toLocaleString(undefined, {
          month: "short",
          day: "numeric",
          hour: "numeric",
          minute: "2-digit",
        })}
      </span>
    ),
  },
]

// basePath    — the URL to navigate to when filters change (e.g. "/:ws/agents/:id" or "/:ws/history")
// getExtraParams — optional callback returning extra query params to always merge (e.g. { agents: [...] }).
//                 Updated synchronously via ref so all internal handlers always see the latest value.
//                 Callers can also pass extra: { ... } directly in navigate overrides to override the callback.
export function useRunsFilters({ basePath, filters, pagination, getExtraParams }) {
  const [search, setSearch] = useState(filters.search || "")
  const [statuses, setStatuses] = useState(filters.statuses || [])
  const [responses, setResponses] = useState(filters.responses || [])
  const [from, setFrom] = useState(filters.from || "")
  const [to, setTo] = useState(filters.to || "")
  const [perPage, setPerPage] = useState(pagination.limit || 50)
  const searchDebounceRef = useRef(null)

  // Kept in a ref and updated synchronously each render — ensures navigate always reads the
  // latest closure even when called synchronously from event handlers before React re-renders.
  const getExtraParamsRef = useRef(getExtraParams)
  getExtraParamsRef.current = getExtraParams

  const navigate = (overrides = {}) => {
    const { extra = {}, ...rest } = overrides
    const nextSearch = rest.search !== undefined ? rest.search : search
    const nextStatuses = rest.statuses !== undefined ? rest.statuses : statuses
    const nextResponses = rest.responses !== undefined ? rest.responses : responses
    const nextFrom = rest.from !== undefined ? rest.from : from
    const nextTo = rest.to !== undefined ? rest.to : to
    const nextPage = rest.page !== undefined ? rest.page : 1
    const nextPerPage = rest.perPage !== undefined ? rest.perPage : perPage

    const params = {}
    if (nextSearch) params.search = nextSearch
    if (nextStatuses?.length > 0) params.statuses = nextStatuses
    if (nextResponses?.length > 0) params.responses = nextResponses
    if (nextFrom && nextTo) { params.from = nextFrom; params.to = nextTo }
    if (nextPage > 1) params.page = nextPage
    if (nextPerPage !== 50) params.per_page = nextPerPage
    // Callback provides persistent extras (e.g. agentIds); extra arg takes precedence
    Object.assign(params, getExtraParamsRef.current?.() ?? {}, extra)

    router.get(basePath, params, {
      preserveScroll: true,
      preserveState: true,
      replace: true,
    })
  }

  const handleSearchChange = (value) => {
    setSearch(value)
    clearTimeout(searchDebounceRef.current)
    searchDebounceRef.current = setTimeout(() => {
      navigate({ search: value, page: 1 })
    }, 400)
  }

  const handleStatusToggle = (status) => {
    let next
    if (status === "processing") {
      const anySelected = PROCESSING_STATUSES.some(s => statuses.includes(s))
      next = anySelected
        ? statuses.filter(s => !PROCESSING_STATUSES.includes(s))
        : [...statuses.filter(s => !PROCESSING_STATUSES.includes(s)), ...PROCESSING_STATUSES]
    } else {
      next = statuses.includes(status)
        ? statuses.filter(s => s !== status)
        : [...statuses, status]
    }
    setStatuses(next)
    navigate({ statuses: next, page: 1 })
  }

  const handleResponseToggle = (response) => {
    const next = responses.includes(response)
      ? responses.filter(r => r !== response)
      : [...responses, response]
    setResponses(next)
    navigate({ responses: next, page: 1 })
  }

  const handleDateRangeChange = (range) => {
    const newFrom = range?.from ? format(range.from, "yyyy-MM-dd") : ""
    const newTo = range?.to ? format(range.to, "yyyy-MM-dd") : ""
    setFrom(newFrom)
    setTo(newTo)
    if ((newFrom && newTo) || (!newFrom && !newTo)) {
      navigate({ from: newFrom, to: newTo, page: 1 })
    }
  }

  const handleClearDates = () => {
    setFrom("")
    setTo("")
    navigate({ from: "", to: "", page: 1 })
  }

  const handleClearFilters = () => {
    setSearch("")
    setStatuses([])
    setResponses([])
    setFrom("")
    setTo("")
    const params = perPage !== 50 ? { per_page: perPage } : {}
    router.get(basePath, params, {
      preserveScroll: true,
      preserveState: true,
      replace: true,
    })
  }

  const handlePerPageChange = (value) => {
    const n = parseInt(value, 10)
    setPerPage(n)
    navigate({ perPage: n, page: 1 })
  }

  const handleBarDateRange = ({ from: fromDate, to: toDate }) => {
    const fromISO = fromDate.toISOString()
    const toISO = endOfDay(toDate).toISOString()
    setFrom(fromISO)
    setTo(toISO)
    navigate({ from: fromISO, to: toISO, page: 1 })
  }

  const formatDate = (dateStr) => {
    if (!dateStr) return ""
    const date = parseDateStr(dateStr)
    return date.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })
  }

  const hasActiveFilters = !!(search || statuses.length > 0 || responses.length > 0 || (from && to))
  const dateLabel = from && to ? `${formatDate(from)} – ${formatDate(to)}` : "Date"

  return {
    search, statuses, responses, from, to, perPage,
    navigate, hasActiveFilters, dateLabel,
    handleSearchChange, handleStatusToggle, handleResponseToggle,
    handleDateRangeChange, handleBarDateRange, handleClearDates, handleClearFilters,
    handlePerPageChange,
  }
}

// extras — optional node rendered between the date filter and the Clear button (e.g. an Agent dropdown)
export function RunsToolbar({ search, statuses, responses, from, to, hasActiveFilters, dateLabel, handleSearchChange, handleStatusToggle, handleResponseToggle, handleDateRangeChange, handleClearDates, handleClearFilters, extras }) {
  const [dateOpen, setDateOpen] = useState(false)
  const dateRange = (from || to) ? {
    from: parseDateStr(from) ?? undefined,
    to: parseDateStr(to) ?? undefined,
  } : undefined

  const handlePresetSelect = (preset) => {
    handleDateRangeChange(preset.range())
    setDateOpen(false)
  }

  const isPresetActive = (preset) => {
    const r = preset.range()
    const fromDate = parseDateStr(from)
    const toDate = parseDateStr(to)
    if (!fromDate || !toDate) return false
    return format(fromDate, "yyyy-MM-dd") === format(r.from, "yyyy-MM-dd") &&
           format(toDate, "yyyy-MM-dd") === format(r.to, "yyyy-MM-dd")
  }

  return (
    <div className="flex items-center gap-2 flex-wrap">
      <div className="relative flex-1 min-w-[180px] max-w-sm">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
        <Input
          type="search"
          placeholder="Search runs..."
          value={search}
          onChange={e => handleSearchChange(e.target.value)}
          className="pl-8 pr-8 h-8 text-sm [&::-webkit-search-cancel-button]:hidden"
        />
        {search && (
          <button
            type="button"
            onClick={() => handleSearchChange("")}
            className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
            aria-label="Clear search"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        )}
      </div>

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="sm" className={`group/status ${statuses.length > 0 ? "border-slate-400 hover:border-slate-400" : ""}`}>
            <Tag className="h-3.5 w-3.5 text-muted-foreground group-hover/status:text-foreground transition-colors" />
            Status
            {statuses.length > 0 && (
              <Badge variant="secondary" className="ml-0.5 h-4 min-w-4 px-1 text-[10px] rounded-full">{PROCESSING_STATUSES.some(s => statuses.includes(s)) ? statuses.filter(s => !PROCESSING_STATUSES.includes(s)).length + 1 : statuses.length}</Badge>
            )}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-44">
          {STATUSES.map(s => {
            const selected = s.value === "processing"
              ? PROCESSING_STATUSES.some(ps => statuses.includes(ps))
              : statuses.includes(s.value)
            return (
              <DropdownMenuItem
                key={s.value}
                onSelect={(e) => { e.preventDefault(); handleStatusToggle(s.value) }}
                className={`group/sitem justify-between ${selected ? "bg-accent" : ""}`}
              >
                <div className="flex items-center gap-1.5">
                  <RunStatusBadge status={s.value} iconOnly className={s.value === "processing" ? `group-focus/sitem:bg-white ${selected ? "bg-white" : ""}` : undefined} />
                  {s.label}
                </div>
                {selected && <Check className="h-3.5 w-3.5 text-muted-foreground" />}
              </DropdownMenuItem>
            )
          })}
        </DropdownMenuContent>
      </DropdownMenu>

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="sm" className={`group/response ${responses.length > 0 ? "border-slate-400 hover:border-slate-400" : ""}`}>
            <Globe className="h-3.5 w-3.5 text-muted-foreground group-hover/response:text-foreground transition-colors" />
            Response
            {responses.length > 0 && (
              <Badge variant="secondary" className="ml-0.5 h-4 min-w-4 px-1 text-[10px] rounded-full">{responses.length}</Badge>
            )}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-44">
          {RESPONSES.map(r => (
            <DropdownMenuItem
              key={r.value}
              onSelect={(e) => { e.preventDefault(); handleResponseToggle(r.value) }}
              className={`justify-between ${responses.includes(r.value) ? "bg-accent" : ""}`}
            >
              <div className="flex items-center gap-1.5">
                <Badge variant={r.variant} size="small">{r.value}</Badge>
                {r.label}
              </div>
              {responses.includes(r.value) && <Check className="h-3.5 w-3.5 text-muted-foreground" />}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>

      <Popover open={dateOpen} onOpenChange={setDateOpen}>
        <PopoverTrigger asChild>
          <Button variant="outline" size="sm" className={`group/date ${from && to ? "border-slate-400 hover:border-slate-400" : ""}`}>
            <CalendarIcon className="h-3.5 w-3.5 text-muted-foreground group-hover/date:text-foreground transition-colors" />
            {dateLabel}
          </Button>
        </PopoverTrigger>
        <PopoverContent align="end" className="w-auto p-0" collisionPadding={8}>
          <div className="flex">
            <div className="flex flex-col border-r p-1 w-36">
              {DATE_PRESETS.map(preset => (
                <Button
                  key={preset.label}
                  variant="ghost"
                  size="sm"
                  className={`justify-start text-sm font-normal h-8 ${isPresetActive(preset) ? "bg-accent text-accent-foreground" : "text-muted-foreground hover:text-foreground"}`}
                  onClick={() => handlePresetSelect(preset)}
                >
                  {preset.label}
                </Button>
              ))}
              {(from || to) && (
                <>
                  <div className="-mx-1 border-t mt-auto mb-1" />
                  <Button
                    variant="ghost"
                    size="sm"
                    className="justify-start text-sm font-normal h-8 text-muted-foreground hover:text-foreground"
                    onClick={() => { handleClearDates(); setDateOpen(false) }}
                  >
                    Clear dates
                  </Button>
                </>
              )}
            </div>
            <Calendar
              mode="range"
              selected={dateRange}
              onSelect={handleDateRangeChange}
              numberOfMonths={2}
              defaultMonth={subMonths(new Date(), 1)}
              disabled={{ after: new Date() }}
            />
          </div>
        </PopoverContent>
      </Popover>

      {extras}

      {hasActiveFilters && (
        <Button variant="ghost" size="sm" onClick={handleClearFilters} className="group/clear text-muted-foreground hover:text-foreground gap-1">
          <X className="h-3.5 w-3.5 text-muted-foreground group-hover/clear:text-foreground transition-colors" />
          Clear
        </Button>
      )}
    </div>
  )
}

export function Runs({ workspace_slug, runs = [], pagination = {}, filterProps, columns: columnsProp, emptyMessage }) {
  const columns = columnsProp ?? COLUMNS
  const { hasActiveFilters, handleClearFilters, perPage, navigate, handlePerPageChange } = filterProps
  const [selectedRun, setSelectedRun] = useState(null)
  const [sheetOpen, setSheetOpen] = useState(false)
  const directionRef = useRef(0)
  const [sortConfig, setSortConfig] = useState({ key: null, direction: null })

  const sortedRuns = useMemo(() => {
    if (!sortConfig.key) return runs
    const { key, direction } = sortConfig
    return [...runs].sort((a, b) => {
      const aVal = a[key] ?? ''
      const bVal = b[key] ?? ''
      const result = String(aVal).localeCompare(String(bVal), undefined, { numeric: true, sensitivity: 'base' })
      return direction === 'asc' ? result : -result
    })
  }, [runs, sortConfig])

  // Sync ?run= URL param so runs can be deep-linked
  useEffect(() => {
    const url = new URL(window.location.href)
    if (sheetOpen && selectedRun) {
      url.searchParams.set("run", String(selectedRun.id))
    } else {
      url.searchParams.delete("run")
    }
    window.history.replaceState(null, "", url.toString())
  }, [sheetOpen, selectedRun?.id])

  // On mount, open the drawer if ?run= is present and the run is on this page
  useEffect(() => {
    const runId = Number(new URLSearchParams(window.location.search).get("run"))
    if (!runId) return
    const run = runs.find(r => r.id === runId)
    if (run) { setSelectedRun(run); setSheetOpen(true) }
  }, [])

  const hasInProgressRuns = runs.some(r => ["pending", "extracting", "requesting"].includes(r.status))

  useEffect(() => {
    if (!hasInProgressRuns) return

    const interval = setInterval(() => {
      router.reload({ only: ["runs", "pagination"] })
    }, 3000)

    return () => clearInterval(interval)
  }, [hasInProgressRuns])

  const { page = 1, pages = 1, count = 0, limit = 50 } = pagination
  const pageNumbers = usePagination({ page, pages })
  const startItem = count === 0 ? 0 : (page - 1) * limit + 1
  const endItem = Math.min(page * limit, count)

  if (!hasActiveFilters && count === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <div className="rounded-full bg-muted p-4">
          <Play className="h-6 w-6 text-muted-foreground" />
        </div>
        <p className="text-md font-medium mt-4 mb-1">No runs yet</p>
        <p className="text-sm text-muted-foreground">
          {emptyMessage ?? `Runs will appear here when emails are sent to inbox@${workspace_slug}.decodekit.ai`}
        </p>
      </div>
    )
  }

  const currentIndex = selectedRun ? sortedRuns.findIndex(r => r.id === selectedRun.id) : -1
  const prevRun = currentIndex > 0 ? sortedRuns[currentIndex - 1] : null
  const nextRun = currentIndex !== -1 && currentIndex < sortedRuns.length - 1 ? sortedRuns[currentIndex + 1] : null

  const handleRowClick = (run) => { directionRef.current = 0; setSelectedRun(run); setSheetOpen(true) }
  const handlePrev = prevRun ? () => { directionRef.current = -1; setSelectedRun(prevRun) } : null
  const handleNext = nextRun ? () => { directionRef.current = 1; setSelectedRun(nextRun) } : null

  return (
    <div className="space-y-3">
      <RunDetailSheet
        run={selectedRun}
        open={sheetOpen}
        onOpenChange={(open) => { if (!open) setSheetOpen(false) }}
        direction={directionRef.current}
        onPrev={handlePrev}
        onNext={handleNext}
      />

      {/* Table or filtered empty state */}
      {runs.length > 0 ? (
        <DataTableReact resizable sortable columns={columns} data={sortedRuns} sortConfig={sortConfig} onSortChange={setSortConfig} onRowClick={handleRowClick} />
      ) : (
        <div className="flex flex-col items-center justify-center py-12 text-center rounded-lg border border-border">
          <p className="text-sm font-medium mb-1">No runs match your filters</p>
          <p className="text-sm text-muted-foreground mb-3">Try adjusting your search or filters</p>
          <Button variant="outline" size="sm" onClick={handleClearFilters}>Clear filters</Button>
        </div>
      )}

      {/* Pagination */}
      {count > 0 && (
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground whitespace-nowrap">Rows per page</span>
            <Select value={String(perPage)} onValueChange={handlePerPageChange}>
              <SelectTrigger size="sm" className="w-auto h-8">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="25">25</SelectItem>
                <SelectItem value="50">50</SelectItem>
                <SelectItem value="100">100</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <span className="text-sm text-muted-foreground whitespace-nowrap">
            {startItem}–{endItem} of {count}
          </span>

          {pages > 1 && (
            <div className="flex items-center gap-1">
              <Button
                variant="outline"
                size="icon-sm"
                disabled={page <= 1}
                onClick={() => navigate({ page: page - 1 })}
                aria-label="Previous page"
              >
                ‹
              </Button>
              {pageNumbers.map((p, i) =>
                p === "..." ? (
                  <span key={`ellipsis-${i}`} className="px-1.5 text-sm text-muted-foreground select-none">…</span>
                ) : (
                  <Button
                    key={p}
                    variant={p === page ? "default" : "outline"}
                    size="icon-sm"
                    onClick={() => navigate({ page: p })}
                    aria-label={`Page ${p}`}
                    aria-current={p === page ? "page" : undefined}
                  >
                    {p}
                  </Button>
                )
              )}
              <Button
                variant="outline"
                size="icon-sm"
                disabled={page >= pages}
                onClick={() => navigate({ page: page + 1 })}
                aria-label="Next page"
              >
                ›
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
